import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChatCircleText } from "@phosphor-icons/react";

const TeamPage = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [team, setTeam] = useState([]);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data.items));
  }, []);

  const startDm = async (uid) => {
    try {
      await api.post("/threads", { kind: "dm", user_id: uid });
      toast.success("Direct message opened.");
      nav("/messages");
    } catch {
      toast.error("Could not open DM.");
    }
  };

  const avgUtil = team.length
    ? Math.round(team.reduce((s, m) => s + m.utilization, 0) / team.length)
    : 0;
  const totalAvail = team.reduce((s, m) => s + m.available_hours, 0);
  const totalEng = team.reduce((s, m) => s + m.active_engagements, 0);

  return (
    <div data-testid="team-page" className="space-y-8">
      <div className="pb-8 border-b border-[#1A2F24]/12">
        <p className="label-eyebrow">Resource Management</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#112018] mt-2">Team Workload</h1>
        <p className="text-[#606C64] mt-2 text-sm max-w-xl">
          A weekly view of utilization, capacity and active engagements across the firm.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 md:gap-6">
        <div className="card-flat">
          <p className="label-eyebrow">Avg utilization</p>
          <p className="font-mono text-5xl mt-3 text-[#A8977B]">{avgUtil}%</p>
        </div>
        <div className="card-flat">
          <p className="label-eyebrow">Available hours / wk</p>
          <p className="font-mono text-5xl mt-3 text-[#112018]">{totalAvail}h</p>
        </div>
        <div className="card-flat">
          <p className="label-eyebrow">Active engagements</p>
          <p className="font-mono text-5xl mt-3 text-[#112018]">{totalEng}</p>
        </div>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F7F6F2] border-b border-[#1A2F24]/12">
            <tr className="text-left">
              <th className="label-eyebrow p-4">Member</th>
              <th className="label-eyebrow p-4">Title</th>
              <th className="label-eyebrow p-4">Utilization</th>
              <th className="label-eyebrow p-4 hidden md:table-cell">Available</th>
              <th className="label-eyebrow p-4 hidden md:table-cell">Engagements</th>
              <th className="label-eyebrow p-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {team.map((m) => {
              const tone =
                m.utilization >= 90
                  ? "#C94A3D"
                  : m.utilization >= 75
                    ? "#A8977B"
                    : "#1A2F24";
              return (
                <tr
                  key={m.id}
                  className="border-b border-[#1A2F24]/8"
                  data-testid={`team-row-${m.id}`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#1A2F24] text-[#F7F6F2] flex items-center justify-center font-mono text-xs">
                        {m.avatar}
                      </div>
                      <span className="text-[#112018]">{m.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-[#606C64] text-sm">{m.title}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-3 w-40">
                      <div className="flex-1 progress-track">
                        <div
                          className="h-full"
                          style={{ width: `${m.utilization}%`, background: tone }}
                        />
                      </div>
                      <span className="font-mono text-xs" style={{ color: tone }}>
                        {m.utilization}%
                      </span>
                    </div>
                  </td>
                  <td className="p-4 hidden md:table-cell font-mono text-sm">
                    {m.available_hours}h
                  </td>
                  <td className="p-4 hidden md:table-cell font-mono text-sm">
                    {m.active_engagements}
                  </td>
                  <td className="p-4 text-right">
                    {m.id !== user?.id ? (
                      <button
                        onClick={() => startDm(m.id)}
                        className="btn-outline inline px-3 py-1.5"
                        data-testid={`message-${m.id}`}
                      >
                        <ChatCircleText size={14} className="inline mr-2" />
                        Message
                      </button>
                    ) : (
                      <span className="label-eyebrow">You</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeamPage;
